// Serviço de API para Photos
import { apiRequest } from '@/services/queryClient';
import { getApiBaseUrl } from '@/config/production';
import type { Photo } from '@/types/schema';
import type {
    PhotoFilters,
    PhotosResponse,
    PhotoSearchParams,
    PhotoUploadData,
    PhotoUpdateData,
    PhotoBatchInfo,
    PhotoWithDetails,
    PhotoUploadProgress,
    PhotoProcessingStatus,
    PhotoStats,
    PhotoSearchResult,
    PhotoDownloadOptions
} from '../models/photo.types';

const API_BASE = `${getApiBaseUrl()}/api`;

export class PhotoService {
    // Buscar todas as fotos com filtros
    static async getAll(filters: PhotoFilters = {}, fullDetail: boolean = false): Promise<PhotosResponse> {
        const params = new URLSearchParams();

        if (filters.eventId) params.append('eventId', filters.eventId);
        if (filters.bib) params.append('bib', filters.bib);
        if (filters.searchTerm) params.append('search', filters.searchTerm);
        if (filters.category) params.append('category', filters.category);
        if (filters.status) params.append('status', filters.status);
        if (filters.visibility) params.append('visibility', filters.visibility);
        if (filters.author) params.append('author', filters.author);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.limit) params.append('limit', filters.limit.toString());
        if (fullDetail) params.append('fullDetail', 'true');

        const response = await apiRequest('GET', `${API_BASE}/photos?${params}`);
        const data = await response.json();

        return {
            photos: data.data?.photos || data.photos || [],
            total: data.data?.total || data.total || 0,
            page: data.data?.page || data.page || filters.page || 1,
            limit: data.data?.limit || data.limit || filters.limit || 10,
            totalPages: data.data?.totalPages || data.totalPages
        };
    }

    // Buscar fotos mais recentes
    static async getLatest(limit = 8): Promise<Photo[]> {
        const response = await apiRequest('GET', `${API_BASE}/photos/latest?limit=${limit}`);
        const data = await response.json();
        return data.data?.photos || data.photos || [];
    }

    // Buscar foto por ID
    static async getById(id: string): Promise<PhotoWithDetails> {
        const response = await apiRequest('GET', `${API_BASE}/photos/${id}`);
        const data = await response.json();
        return data.data || data;
    }

    // Buscar fotos por evento e número do peito
    static async search(params: PhotoSearchParams): Promise<Photo[]> {
        const searchParams = new URLSearchParams();
        
        if (params.eventId) searchParams.append('eventId', params.eventId);
        if (params.bib) searchParams.append('bibNumber', params.bib);
        if (params.tags && params.tags.length > 0) searchParams.append('tags', params.tags.join(','));

        const response = await apiRequest('GET', `${API_BASE}/photos/search?${searchParams}`);
        const data = await response.json();
        return data.data?.photos || data.photos || [];
    }

    // Buscar fotos por evento (otimizado por padrão)
    static async getByEvent(eventId: string, page = 1, limit = 12, fullDetail: boolean = false): Promise<PhotosResponse> {
        const params = new URLSearchParams({
            eventId,
            page: page.toString(),
            limit: limit.toString()
        });

        // Adicionar fullDetail apenas se for true
        if (fullDetail) {
            params.append('fullDetail', 'true');
        }

        const url = `${API_BASE}/photos?${params}`;
        const response = await apiRequest('GET', url);
        const data = await response.json();

        return {
            photos: data.data?.photos || data.photos || [],
            total: data.data?.total || data.total || 0,
            page: data.data?.page || data.page || page,
            limit: data.data?.limit || data.limit || limit,
            totalPages: data.data?.totalPages || data.totalPages
        };
    }

    // Buscar fotos relacionadas
    static async getRelated(photoId: string, limit = 6): Promise<Photo[]> {
        const response = await apiRequest('GET', `${API_BASE}/photos/${photoId}/related?limit=${limit}`);
        const data = await response.json();
        return data.data?.photos || data.photos || [];
    }

    // Atualizar foto
    static async update(updateData: PhotoUpdateData): Promise<Photo> {
        const { id, ...data } = updateData;
        const response = await apiRequest('PUT', `${API_BASE}/photos/${id}`, data);
        return response.json();
    }

    // Deletar foto
    static async delete(id: string): Promise<void> {
        await apiRequest('DELETE', `${API_BASE}/photos/${id}`);
    }

    // Upload de fotos
    static async upload(uploadData: PhotoUploadData): Promise<{ success: boolean; photos: Photo[]; errors?: string[] }> {
        const formData = new FormData();

        // Adicionar arquivos
        uploadData.files.forEach((file, index) => {
            formData.append(`files`, file);
        });

        // Adicionar metadados
        formData.append('eventId', uploadData.eventId);
        if (uploadData.category) formData.append('category', uploadData.category);
        if (uploadData.description) formData.append('description', uploadData.description);
        if (uploadData.metadata) formData.append('metadata', JSON.stringify(uploadData.metadata));

        const response = await apiRequest('POST', `${API_BASE}/photos/upload`, formData, {
            'Content-Type': 'multipart/form-data'
        });

        return response.json();
    }

    // Upload com progresso
    static async uploadWithProgress(
        uploadData: PhotoUploadData,
        onProgress?: (progress: PhotoUploadProgress[]) => void
    ): Promise<{ success: boolean; photos: Photo[]; errors?: string[] }> {
        // Implementação com progresso seria feita com XMLHttpRequest
        // Por enquanto, usando o método simples
        return this.upload(uploadData);
    }

    // Buscar informações de batch
    static async getBatchInfo(batchId: string): Promise<PhotoBatchInfo> {
        const response = await apiRequest('GET', `${API_BASE}/photo-batches/${batchId}`);
        const data = await response.json();
        return data.data || data;
    }

    // Buscar fotos de um batch
    static async getBatchPhotos(batchId: string, page = 1, limit = 10, searchTerm = ''): Promise<PhotosResponse> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            ...(searchTerm && { search: searchTerm })
        });

        const response = await apiRequest('GET', `${API_BASE}/photo-batches/${batchId}/photos?${params}`);
        const data = await response.json();

        return {
            photos: data.data?.photos || data.photos || [],
            total: data.data?.total || data.total || 0,
            page: data.data?.page || data.page || page,
            limit: data.data?.limit || data.limit || limit,
            totalPages: data.data?.totalPages || data.totalPages
        };
    }

    // Buscar estatísticas de fotos
    static async getStats(): Promise<PhotoStats> {
        const response = await apiRequest('GET', `${API_BASE}/photos/stats`);
        const data = await response.json();
        return data.data || data;
    }

    // Buscar status de processamento
    static async getProcessingStatus(photoId: string): Promise<PhotoProcessingStatus> {
        const response = await apiRequest('GET', `${API_BASE}/photos/${photoId}/processing-status`);
        const data = await response.json();
        return data.data || data;
    }

    // Download de foto
    static async download(photoId: string, options: PhotoDownloadOptions = {}): Promise<Blob> {
        const params = new URLSearchParams();
        if (options.format) params.append('format', options.format);
        if (options.quality) params.append('quality', options.quality);
        if (options.watermark !== undefined) params.append('watermark', options.watermark.toString());

        const response = await apiRequest('GET', `${API_BASE}/photos/${photoId}/download?${params}`);
        return response.blob();
    }

    // Buscar fotos com eventos (combinado)
    static async getPhotosWithEvents(limit = 8): Promise<PhotoWithDetails[]> {
        const [photosResponse, eventsResponse] = await Promise.all([
            this.getLatest(limit),
            apiRequest('GET', `${API_BASE}/events?limit=50`).then(r => r.json())
        ]);

        const events = eventsResponse.data?.events || eventsResponse.events || [];

        return photosResponse.map(photo => ({
            ...photo,
            event: events.find((event: any) => event.id === photo.eventId)
        }));
    }

    // Verificar se a foto deve ter marca d'água
    static shouldWatermark(photo: Photo | Partial<Photo>): boolean {
        // Lógica para decidir se mostra marca d'água
        // Por padrão, todas as fotos públicas em listagens devem ter marca d'água
        // Futuramente pode verificar se o usuário comprou a foto
        return true;
    }
}
